/**
 * TurnstileWidget — Cloudflare Turnstile CAPTCHA integration.
 * Lazy-loads the Turnstile script, renders managed widget.
 */

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = (import.meta as unknown as Record<string, Record<string, string>>).env?.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAA_placeholder';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  getResponse: (widgetId: string) => string | undefined;
}

let _scriptLoaded = false;
let _scriptLoading: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (_scriptLoaded) return Promise.resolve();
  if (_scriptLoading) return _scriptLoading;

  _scriptLoading = new Promise<void>((resolve, reject) => {
    // Check if already in DOM
    if ((window as unknown as Record<string, unknown>).turnstile) {
      _scriptLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      _scriptLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(script);
  });

  return _scriptLoading;
}

function getTurnstile(): TurnstileApi | null {
  return (window as unknown as Record<string, TurnstileApi>).turnstile || null;
}

export interface TurnstileInstance {
  container: HTMLElement;
  getToken: () => string | null;
  reset: () => void;
  destroy: () => void;
}

/**
 * Create a Turnstile widget. Returns controls for getting the token.
 */
export async function createTurnstile(isDark: boolean): Promise<TurnstileInstance> {
  const container = document.createElement('div');
  container.className = 'auth-turnstile';

  let widgetId: string | null = null;
  let resolvedToken: string | null = null;

  // If site key is placeholder, return a no-op widget
  if (SITE_KEY.includes('placeholder')) {
    return {
      container,
      getToken: () => null,
      reset: () => {},
      destroy: () => {},
    };
  }

  try {
    await loadScript();
    const api = getTurnstile();
    if (!api) throw new Error('Turnstile API not available');

    const widgetEl = document.createElement('div');
    container.appendChild(widgetEl);

    widgetId = api.render(widgetEl, {
      sitekey: SITE_KEY,
      theme: isDark ? 'dark' : 'light',
      callback: (token: string) => { resolvedToken = token; },
      'expired-callback': () => { resolvedToken = null; },
      'error-callback': () => { resolvedToken = null; },
    });
  } catch {
    // Turnstile failed to load — degrade gracefully
    container.innerHTML = '';
  }

  return {
    container,
    getToken: () => {
      if (resolvedToken) return resolvedToken;
      if (widgetId) {
        const api = getTurnstile();
        return api?.getResponse(widgetId) || null;
      }
      return null;
    },
    reset: () => {
      resolvedToken = null;
      if (widgetId) {
        const api = getTurnstile();
        api?.reset(widgetId);
      }
    },
    destroy: () => {
      if (widgetId) {
        const api = getTurnstile();
        api?.remove(widgetId);
        widgetId = null;
      }
    },
  };
}
